import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import {
  ExternalHomologation,
  ExternalHomologationID,
  ExternalHomologationResponse,
  InternalHomologation,
  InternalHomologationById,
  InternalHomologationResponse,
  Person,
  PersonResponse,
  PreconfigSubjects,
  StudentRecordSubjects,
  SubjectCurriculum,
  UpdatePayload,
  InternalHomologationSubjectPayload,
} from '../interfaces/homologations.interface';
import { HomologationPerSubjectPayload, UpdateExtHom } from '../interfaces/payload.interface';

import {
  setInternatHomologationsList,
  setExternalHomologationsList,
} from '../slices/homologationsSlice';

// const downloader = require('downloadjs');

export const HomologationsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: [
    'InternalHomologation',
    'MattersSubjects',
    'StudentRecord',
    'PersonsAvailables',
    'ExternalHomologationPreConfig',
    'Applications',
    'HomologateInternalSubjects',
  ],
});
// Define a service using a base URL and expected endpoints
export const HomologationsApi = HomologationsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all homologation applications
     */
    getApplicationsHomologations: builder.query<
      InternalHomologationResponse | ExternalHomologationResponse,
      {
        profileId: number;
        acronym: 'HOMEXT' | 'CAMCAR';
        page: number;
        perPage: number;
        search: string;
        startDate?: string;
        endDate?: string;
        status?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { profileId, acronym, page, search, perPage, startDate, endDate, status, sort, type_sort },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<
            InternalHomologationResponse | ExternalHomologationResponse
          >(`/application/get-applications-next-status/pending`, {
            params: {
              user_profile_id: profileId,
              acronym,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              search,
              start_date: startDate,
              end_date: endDate,
              status,
            },
          });

          if (acronym === 'CAMCAR')
            dispatch(setInternatHomologationsList(data.data as InternalHomologation[]));
          if (acronym === 'HOMEXT')
            dispatch(setExternalHomologationsList(data.data as ExternalHomologation[]));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'InternalHomologation', id } as const)),
              { type: 'InternalHomologation', id: 'LIST' },
            ]
          : [{ type: 'InternalHomologation', id: 'LIST' }],
    }),

    /**
     * @GET persons to combo box
     * external homologation
     */
    getAvailablePersons: builder.query<Person[], { profileId: number; search: string }>({
      queryFn: async ({ profileId, search }) => {
        try {
          const { data } = await mainApi.get<PersonResponse>('/persons', {
            params: {
              user_profile_id: profileId,
              // data: 'all',
              search,
            },
          });

          return { data: data.data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'PersonsAvailables', id: 'COMBO_LIST' }],
    }),

    /**
     * @GET application by id
     */
    getApplicationHomologation: builder.query<
      InternalHomologationById,
      { profileId: number; ApplicationHomId: number }
    >({
      queryFn: async ({ profileId, ApplicationHomId }) => {
        try {
          const { data } = await mainApi.get<InternalHomologationById>(
            `/application/${ApplicationHomId}`,
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'InternalHomologation', id: result?.id },
      ],
    }),

    /**
     * @GET matters homologation by education level
     */
    getEducationLevelMatters: builder.query<
      SubjectCurriculum[],
      { profileId?: number; edLvlId: number; curriculum_id?: number }
    >({
      queryFn: async ({ profileId, edLvlId, curriculum_id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<SubjectCurriculum[]>(
            `/education-levels/${edLvlId}/internal-homologation-subject-curriculum`,
            {
              params: {
                user_profile_id,
                ...(curriculum_id && { curriculum_id }),
                data: 'all',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'MattersSubjects', profileId }],
    }),

    /**
     * @POST create internal homologation subject
     */
    addInternalHomologationSubject: builder.mutation<
      InternalHomologationById,
      {
        profileId: number;
        InternalHomologationSubjectPayload: Partial<InternalHomologationSubjectPayload>;
      }
    >({
      queryFn: async ({ profileId, InternalHomologationSubjectPayload }) => {
        try {
          const { data } = await mainApi.post<InternalHomologationById>('/internal-homologation', {
            user_profile_id: profileId,
            ...InternalHomologationSubjectPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'HomologateInternalSubjects', id: 'LIST' }],
    }),

    /**
     * @PUT Update internal homologation subject
     */
    updateInternalHomologationSubject: builder.mutation<
      InternalHomologationById,
      {
        profileId: number;
        internalHomologationId: number;
        InternalHomologationSubjectPayload: Partial<InternalHomologationSubjectPayload>;
      }
    >({
      queryFn: async ({
        profileId,
        InternalHomologationSubjectPayload,
        internalHomologationId,
      }) => {
        try {
          const { data } = await mainApi.put<InternalHomologationById>(
            `/internal-homologation/${internalHomologationId}`,
            {
              user_profile_id: profileId,
              ...InternalHomologationSubjectPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'HomologateInternalSubjects', id: 'LIST' }],
    }),

    /**
     * @GET approved subjects by student record
     */
    getStudentRecordSubjects: builder.query<
      StudentRecordSubjects[],
      { profileId: number; stdRecord: number }
    >({
      queryFn: async ({ profileId, stdRecord }) => {
        try {
          const { data } = await mainApi.get<StudentRecordSubjects[]>(
            `/students/student-record/${stdRecord}/subject-approved`,
            {
              params: {
                user_profile_id: profileId,
                data: 'all',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'StudentRecord', profileId }],
    }),

    /**
     * @POST create period type
     */
    // addPeriodType: builder.mutation<
    //   PeriodType,
    //   { profileId: number; periodPayload: Partial<PeriodType> }
    // >({
    //   queryFn: async ({ profileId, periodPayload }) => {
    //     try {
    //       const { data } = await mainApi.post<PeriodType>('/type-periods', {
    //         user_profile_id: profileId,
    //         ...periodPayload,
    //       });

    //       return { data };
    //     } catch (error: any) {
    //       return { error };
    //     }
    //   },
    //   invalidatesTags: [{ type: 'InternalHomologation', id: 'LIST' }],
    // }),

    /**
     * @PUT update application
     */
    updateApplication: builder.mutation<
      UpdatePayload,
      { profileId: number; applicationPayload: Partial<UpdatePayload> }
    >({
      queryFn: async ({ profileId, applicationPayload }) => {
        try {
          const { data } = await mainApi.post<UpdatePayload>(
            `/applications/internal-homologations/change-status`,
            {
              user_profile_id: profileId,
              ...applicationPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'InternalHomologation', id: 'LIST' }],
    }),

    //External Homologation

    /**
     * @GET external application by id
     */
    getExtAppById: builder.query<
      ExternalHomologationID,
      { profileId: number; ApplicationHomId: number }
    >({
      queryFn: async ({ profileId, ApplicationHomId }) => {
        try {
          const { data } = await mainApi.get<ExternalHomologationID>(
            `/applications/external-homologations/${ApplicationHomId}`,
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { ApplicationHomId }) => [
        { type: 'ExternalHomologationPreConfig', id: result?.id },
        { type: 'Applications', id: ApplicationHomId },
      ],
    }),

    /**
     * @GET external application pdf
     */
    getExtAppPdfById: builder.query<any, { profileId: number; ApplicationHomId: number }>({
      queryFn: async ({ profileId, ApplicationHomId }) => {
        try {
          const { data } = await mainApi.get<any>(
            `/applications/external-homologations/${ApplicationHomId}/pdf`,
            {
              responseType: 'arraybuffer',
              headers: {
                Accept: 'application/pdf',
                'Content-Type': 'application/json',
              },
              params: {
                user_profile_id: profileId,
              },
            }
          );

          // const newPdf =

          // const arr = new Uint8Array(request.response.length);
          // request.response.split('').forEach(function (a: any, b: any) {
          //   arr[b] = a.charCodeAt();
          // });

          // downloader(request.response, 'prueba.pdf', headers['content-type']);
          // downloader(arr, 'prueba.pdf', headers['content-type']);
          // downloader(
          //   `data:application/pdf,${request.response}`,
          //   'prueba.pdf',
          //   headers['content-type']
          // );
          // downloader(file, 'prueba.pdf', headers['content-type']);

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'InternalHomologation', id: result?.id },
      ],
    }),

    /**
     * @GET preconfigured subjects
     */
    getPreconfigSubjects: builder.query<
      PreconfigSubjects[],
      { profileId: number; curriculumId: number; instituteId: number }
    >({
      queryFn: async ({ profileId, curriculumId, instituteId }) => {
        try {
          const { data } = await mainApi.get<PreconfigSubjects[]>(
            `/external-homologations/preconfigured`,
            {
              params: {
                user_profile_id: profileId,
                curriculum_id: curriculumId,
                institute_id: instituteId,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'ExternalHomologationPreConfig', id: 'LIST' },
      ],
    }),

    /**
     * @POST Create homologation per subject -> instituteSubject
     */
    addSubInstHomologation: builder.mutation<
      HomologationPerSubjectPayload,
      { profileId: number; subjectHomologationPayload: Partial<HomologationPerSubjectPayload> }
    >({
      queryFn: async ({ profileId, subjectHomologationPayload }) => {
        try {
          const { data } = await mainApi.post<HomologationPerSubjectPayload>(
            '/external-homologations',
            {
              user_profile_id: profileId,
              ...subjectHomologationPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'ExternalHomologationPreConfig', id: 'LIST' }], //cambiar ids por id de cada tabla preconfig
    }),

    /**
     * @PUT update external homologation application
     */
    updateExtHomApp: builder.mutation<
      UpdateExtHom,
      {
        profileId: number;
        extHomId: number;
        subjectHomologationPayload: Partial<UpdateExtHom>;
      }
    >({
      queryFn: async ({ profileId, subjectHomologationPayload, extHomId }) => {
        try {
          const { data } = await mainApi.put<UpdateExtHom>(
            `/applications/external-homologations/${extHomId}`,
            {
              user_profile_id: profileId,
              ...subjectHomologationPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { extHomId }) => [
        { type: 'ExternalHomologationPreConfig', id: extHomId },
      ],
    }),

    /**
     * @DELETE remove preconfig subject
     */
    deletePreconfigSubject: builder.mutation<
      PreconfigSubjects,
      { profileId: number; subjectId: number }
    >({
      queryFn: async ({ profileId, subjectId }) => {
        try {
          const { data } = await mainApi.delete<PreconfigSubjects>(
            `/external-homologations/${subjectId}`,
            {
              data: {
                user_profile_id: profileId,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'ExternalHomologationPreConfig', id: 'LIST' }],
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetApplicationsHomologationsQuery,
  useUpdateApplicationMutation,
  useGetApplicationHomologationQuery,
  useGetEducationLevelMattersQuery,
  useGetStudentRecordSubjectsQuery,
  useGetAvailablePersonsQuery,
  useAddSubInstHomologationMutation,
  useGetPreconfigSubjectsQuery,
  useUpdateExtHomAppMutation,
  useDeletePreconfigSubjectMutation,
  useGetExtAppByIdQuery,
  useGetExtAppPdfByIdQuery,
  useLazyGetExtAppPdfByIdQuery,
  useAddInternalHomologationSubjectMutation,
  useUpdateInternalHomologationSubjectMutation,
} = HomologationsApi;
